<template>
  <div class="stc-root">
    <div class="stc-card">
      <div class="stc-header">
        <button type="button" class="stc-back-btn" @click="goBackToList" aria-label="Back to support tickets">
          <i class="fa fa-chevron-left" aria-hidden="true"></i>
        </button>
        <h3 class="stc-title">Create a Support Ticket</h3>
      </div>

      <form @submit.prevent="saveSupportTicket" class="stc-form">
        <div class="stc-grid">
          <div class="stc-section-head">Account</div>
          <div class="stc-field">
            <label>Account</label>
            <b-form-select v-if="effectiveRole === 'admin'" v-model="selected_tenant_id" :options="user.tenant_options"/>
            <div v-else class="stc-value">{{ getOptionText(user.tenant_options, selected_tenant_id) }}</div>
          </div>
          <div class="stc-field">
            <label>Role</label>
            <b-form-input type="text" v-model="model.creator_role"/>
            <div class="stc-help-note">Your position — helps us tailor responses.</div>
          </div>
          <div class="stc-field std-field--full stc-field--full">
            <label>Additional Contacts</label>
            <b-form-input type="text" v-model="model.additional_contacts" placeholder="comma separated emails"/>
          </div>

          <!-- admin triage: single cream + gold-pipe panel that matches
               the admin-only-row treatment from the FA settings page.
               wrapping all admin fields in one container keeps the
               "admin tab" visual unified rather than striping each
               field individually. -->
          <div v-if="effectiveRole === 'admin'" class="stc-admin-section stc-field--full">
            <div class="stc-section-head stc-section-head--admin">Triage</div>
            <div class="stc-admin-grid">
              <div class="stc-field">
                <label>Created By</label>
                <b-form-select v-model="model.username" :options="user_options"/>
              </div>
              <div class="stc-field">
                <label>Assigned</label>
                <b-form-select v-model="model.assigned_to" :options="assignedOptions"/>
              </div>
              <div class="stc-field stc-field--full">
                <label>Internal Notes</label>
                <b-form-textarea v-model="model.internal_notes" :rows="2" max-rows="5" no-resize class="stc-grow-textarea"/>
                <div class="stc-help-note">Not visible to the customer.</div>
              </div>
            </div>
          </div>

          <div class="stc-section-head">Ticket</div>
          <div class="stc-field">
            <label>Feature</label>
            <b-form-select v-model="model.category" :options="categoryOptions"/>
          </div>
          <div class="stc-field">
            <label>Priority</label>
            <b-form-select v-model="model.priority" :options="priorityOptions"/>
          </div>

          <div class="stc-section-head">Content</div>
          <div class="stc-field stc-field--full">
            <label>Subject</label>
            <b-form-input type="text" v-model="model.subject"/>
          </div>
          <div class="stc-field stc-field--full">
            <label>Description</label>
            <b-form-textarea v-model="model.description" :rows="6" max-rows="24" no-resize class="stc-grow-textarea"/>
            <div class="stc-help-note">Text only — use the Attachments section below for images.</div>
          </div>
          <div class="stc-field stc-field--full">
            <label>Attachments</label>
              <input
                ref="ticketAttachmentInput"
                type="file"
                multiple
                style="display: none"
                @change="handleFileUpload"
              />
              <div class="d-flex align-items-center flex-wrap gap-2">
                <b-button size="sm" variant="primary" @click="$refs.ticketAttachmentInput.click()" :disabled="isSubmitting">
                  <i class="fa fa-paperclip" aria-hidden="true"></i> Add Attachment(s)
                </b-button>
                <b-button
                  v-if="attachmentItems.length > 0"
                  size="sm"
                  variant="outline-danger"
                  @click="clearAttachmentItems"
                  :disabled="isSubmitting"
                >
                  Clear All
                </b-button>
              </div>
              <div v-if="attachmentItems.length === 0" class="text-muted small mt-2">
                No attachments selected yet.
              </div>
              <div v-else class="attachment-grid mt-3">
                <div
                  v-for="item in attachmentItems"
                  :key="item.id"
                  class="attachment-item queued-attachment"
                  :class="'attachment-state-' + item.status"
                >
                  <button
                    v-if="item.status === 'queued' || item.status === 'failed'"
                    type="button"
                    class="attachment-remove"
                    @click="removeAttachmentItem(item.id)"
                  >
                    &times;
                  </button>
                  <img
                    v-if="item.previewUrl"
                    :src="item.previewUrl"
                    class="attachment-thumbnail"
                    :alt="item.name"
                  />
                  <div v-else class="attachment-file">
                    <span style="font-size: 1.4rem;">&#128196;</span>
                    <span class="attachment-filename">{{ item.name }}</span>
                  </div>
                  <div v-if="item.status === 'uploading' || item.status === 'failed'" class="attachment-meta">
                    <div class="attachment-status">
                      <span v-if="item.status === 'uploading'">Uploading {{ item.progress }}%</span>
                      <span v-else>Upload failed</span>
                    </div>
                    <b-progress
                      v-if="item.status === 'uploading'"
                      :value="item.progress"
                      :max="100"
                      height="6px"
                      class="mt-2"
                    />
                    <div v-if="item.error" class="attachment-error">{{ item.error }}</div>
                  </div>
                </div>
              </div>
          </div>
        </div>

        <div class="stc-footer">
          <b-btn type="submit" variant="success" size="sm" :disabled="isSubmitting">Create</b-btn>
          <b-button variant="outline-secondary" size="sm" :to="{ name: 'support_ticket_list' }">Cancel</b-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import {
  create_support_ticket,
  get_primary_users,
  presign_support_ticket_attachment,
  finalize_support_ticket_attachment
} from '../networking/apis'
import {mapState} from "vuex";

export default {
  name: 'support_ticket_create',
  components: {  },
  data () {
    return {
      selected_tenant_id: null,
      user_options: [],
      assignedOptions: [
        { value: null, text: 'Unassigned' },
        { value: 'ben@initekconsulting.com', text: 'Ben ODay' },
        // jen is a legacy short-username account — see support_ticket_details.vue assigned_options.
        { value: 'jen', text: 'Jen' },
        { value: 'chaz@initekconsulting.com', text: 'Chaz Watson' },
        { value: 'kai@initekconsulting.com', text: 'Kai Klein' },
        { value: 'evan@initekconsulting.com', text: 'Evan Anderson' },
      ],
      model: { 'category': 'general', 'priority': 'question'},
      attachmentItems: [],
      isSubmitting: false,
      categoryOptions: [
        {value: 'general', text: 'General'},
        {value: 'actuals', text: 'Actuals'},
        {value: 'alerts', text: 'Alerts'},
        {value: 'maps', text: 'Map Views'},
        {value: 'reports', text: 'View & Reports'},
        {value: 'vsm', text: 'Vehicle Status Manager'},
        {value: 'data', text: 'Data Services'},
        {value: 'fleet-assistant', text: 'Fleet Assistant'},
      ],
      priorityOptions: [
        {value: 'question', text: 'Question/Request'},
        {value: 'minor', text: 'Minor Bug'},
        {value: 'urgent', text: 'Major Bug / System Issue'},
        {value: 'enhancement', text: 'Enhancement Request'},
        {value: 'feature', text: 'New Feature Request'},
      ],
    }
  },
  async created () {
    this.selected_tenant_id = this.user.tenant
    if (this.$route.query.category) this.model.category = this.$route.query.category
    if (this.$route.query.subject)  this.model.subject  = this.$route.query.subject
    if (this.user.role == 'admin')
      await this.load_users()
  },
  beforeDestroy() {
    this.revokeAttachmentPreviews()
  },
  methods: {
    // use browser history.back() so Forward still navigates to this
    // create page after canceling out. fall back to clearing query
    // params if there's no prior in-app history (deep link case).
    goBackToList() {
      if (window.history.length > 1) {
        this.$router.go(-1)
        return
      }
      const q = { ...this.$route.query }
      delete q.ticket_id
      delete q.tenant_id
      delete q.id
      this.$router.replace({ query: q }).catch(() => {})
    },
    getOptionText(options, value) {
      const option = options.find(opt => opt.value === value)
      return option ? option.text : value
    },
    async load_users() {
      const {value: {data = []}} = await get_primary_users(this.selected_tenant_id)
      if (data.results.length > 0) {
        this.user_options = data.results.map(item => ({
          value: item['username'],
          text: item['first_name'] ? item['first_name'] + ' ' + item['last_name'] + ' - ' + item['username'] : item['username']
        }));
        this.user_options.concat(this.assignedOptions)
      } else {
        this.user_options = this.assignedOptions
      }
    },
    async saveSupportTicket () {
      try{
        this.isSubmitting = true
        this.$emit('showAlert', "waiting for response...", 'info')
        const {value: {data = []}} = await create_support_ticket(this.selected_tenant_id,this.model)
        this.model.id = data.ticket_id

        const failedAttachmentCount = await this.saveAttachments()

        if (failedAttachmentCount > 0) {
          this.$emit('showAlert', `Support ticket created, but ${failedAttachmentCount} attachment(s) failed to upload. You can retry from the ticket details screen.`, 'warning', 7000)
          this.$router.push({ name: 'support_ticket_details', query: { tenant_id: this.selected_tenant_id, id: this.model.id } })
          return
        }

        this.$emit('showAlert', data.message || 'Success!', 'success', 1500)
        this.$router.push({name:'support_ticket_list'})
      } catch(e) {
        this.$emit('showAlert', 'error creating ticket, contact support if needed', 'danger', 5000)
      } finally {
        this.isSubmitting = false
      }
    },
    handleFileUpload(event) {
      const files = event && event.target && event.target.files ? Array.from(event.target.files) : []
      if (event && event.target) {
        event.target.value = null
      }
      if (!files.length) return

      const existingKeys = new Set(this.attachmentItems.map(item => `${item.name}:${item.size}`))
      const newItems = files
        .filter(file => !existingKeys.has(`${file.name}:${file.size}`))
        .map((file, index) => this.createAttachmentItem(file, index))
      this.attachmentItems = [...this.attachmentItems, ...newItems]
    },
    createAttachmentItem(file, index) {
      return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}-${index}`,
        file,
        name: file.name,
        size: file.size,
        status: 'queued',
        progress: 0,
        error: null,
        previewUrl: file.type && file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      }
    },
    removeAttachmentItem(itemId) {
      const item = this.attachmentItems.find(candidate => candidate.id === itemId)
      if (item && item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl)
      }
      this.attachmentItems = this.attachmentItems.filter(candidate => candidate.id !== itemId)
    },
    clearAttachmentItems() {
      this.revokeAttachmentPreviews()
      this.attachmentItems = []
    },
    revokeAttachmentPreviews() {
      this.attachmentItems.forEach(item => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl)
        }
      })
    },
    async saveAttachments() {
      const queuedItems = this.attachmentItems.filter(item => item.status === 'queued' || item.status === 'failed')
      if (!queuedItems.length) {
        return 0
      }

      this.$emit('showAlert', "Uploading support ticket attachments...", 'info')

      const CONCURRENCY = 3
      let index = 0
      const worker = async () => {
        while (index < queuedItems.length) {
          const item = queuedItems[index++]
          await this.uploadAttachmentItem(item)
        }
      }
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, queuedItems.length) }, () => worker())
      )

      return this.attachmentItems.filter(item => item.status === 'failed').length
    },
    async uploadAttachmentItem(item) {
      const attachmentIndex = this.attachmentItems.findIndex(candidate => candidate.id === item.id)
      if (attachmentIndex === -1) return

      this.$set(this.attachmentItems, attachmentIndex, {
        ...this.attachmentItems[attachmentIndex],
        status: 'uploading',
        progress: 0,
        error: null,
      })

      try {
        const file = item.file
        const filename = file.name || 'attachment'
        const contentType = file.type || 'application/octet-stream'

        const presignResp = await presign_support_ticket_attachment(this.selected_tenant_id, {
          filename,
          content_type: contentType,
          file_size: file.size || 0,
        })
        const presignData = presignResp && presignResp.value && presignResp.value.data
          ? (presignResp.value.data.data || presignResp.value.data)
          : {}
        const upload = presignData.upload || {}
        const s3Url = presignData.s3_url

        const uploadForm = new FormData()
        Object.entries(upload.fields || {}).forEach(([k, v]) => uploadForm.append(k, v))
        uploadForm.append('file', file)

        await this.uploadToS3WithProgress(upload.url, uploadForm, (pct) => {
          this.updateAttachmentItem(item.id, { progress: pct })
        })

        await finalize_support_ticket_attachment(this.selected_tenant_id, {
          ticket_id: this.model.id,
          attachment_url: s3Url,
        })

        this.updateAttachmentItem(item.id, {
          status: 'uploaded',
          progress: 100,
          error: null,
        })
      } catch (err) {
        const msg = err && err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : (err && err.message ? err.message : 'Upload failed')
        console.warn('[Support Ticket] Presign upload error', err)
        this.updateAttachmentItem(item.id, {
          status: 'failed',
          progress: 0,
          error: msg,
        })
      }
    },

    uploadToS3WithProgress(url, formData, onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', url, true)
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            let detail = `S3 upload failed (HTTP ${xhr.status})`
            try {
              const xml = xhr.responseText || ''
              const codeMatch = xml.match(/<Code>([^<]+)<\/Code>/)
              const msgMatch = xml.match(/<Message>([^<]+)<\/Message>/)
              if (codeMatch || msgMatch) {
                detail = [codeMatch && codeMatch[1], msgMatch && msgMatch[1]].filter(Boolean).join(': ')
              }
            } catch (_) { /* ignore parse errors */ }
            reject(new Error(detail))
          }
        }
        xhr.onerror = () => reject(new Error('S3 upload failed (network error)'))
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable || typeof onProgress !== 'function') return
          const pct = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
          onProgress(pct)
        }
        xhr.send(formData)
      })
    },
    updateAttachmentItem(itemId, patch) {
      const index = this.attachmentItems.findIndex(item => item.id === itemId)
      if (index === -1) return
      this.$set(this.attachmentItems, index, {
        ...this.attachmentItems[index],
        ...patch,
      })
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    // respect MUGGLE "view as non-admin" so admins previewing the UI as
    // a customer don't see admin-only form fields. gate all visibility
    // on effectiveRole, not user.role. backend / fetch calls can still
    // use user.role since muggle is display-only.
    effectiveRole() {
      const viewAs = this.$store && this.$store.state && this.$store.state.profile && this.$store.state.profile.view_as
      if (this.user && this.user.role === 'admin' && viewAs === 'non-admin') return 'customer'
      return this.user && this.user.role
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_users()
      }
    },
    selected_tenant_id() {
      if (this.user.role == 'admin')
        this.load_users()
    }
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}

/* support ticket create — mirrors support_ticket_details.vue (itself
   modeled on tenant_config_fleet_assistant / user_detail): 3-column
   label|control|help table rows, steelblue section titles with an
   <hr>, admin-only rows flagged cream + gold left-pipe. mobile ≤900px
   collapses the table into a stacked grid. */
.stc-root {
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  padding: 12px 32px 32px 32px;
  /* Bootstrap's global card text-align inheritance has leaked onto
     this page before; lock to left so nothing drifts back to center. */
  text-align: left;
}
@media (max-width: 720px) {
  .stc-root { padding: 12px 16px 24px 16px; }
}
.stc-card {
  font-weight: normal;
  text-align: left;
}
.stc-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 18px;
  text-align: left;
}
.stc-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  text-align: left;
}
/* circular back button — matches the details page. drops out of the
   list query params and returns the help-center parent to the list. */
.stc-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #d4d8dc;
  background: #fff;
  color: #495057;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}
.stc-back-btn:hover,
.stc-back-btn:focus {
  background: #f0f5ff;
  border-color: #4a7ba7;
  color: #1f4a73;
  outline: none;
}

.stc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px 18px;
  align-items: start;
  margin-top: 4px;
  text-align: left;
}
@media (max-width: 560px) {
  .stc-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 360px) {
  .stc-grid { grid-template-columns: 1fr; }
}
.stc-section-head {
  grid-column: 1 / -1;
  color: steelblue;
  font-weight: bold;
  font-size: 18px;
  padding-top: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e0e0e0;
  margin-top: 4px;
  text-align: left;
}
.stc-section-head:first-child { padding-top: 0; margin-top: 0; }

.stc-field { min-width: 0; text-align: left; }
.stc-field > label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
  text-align: left;
}
.stc-field--full { grid-column: 1 / -1; }

/* admin triage panel — mirrors the admin-only-row treatment from
   the FA settings page (tenant_config_fleet_assistant.vue): cream
   fill + gold inset pipe via box-shadow on the left edge. the
   nested grid lets admin fields flow side-by-side the same way as
   the outer form. */
.stc-admin-section {
  grid-column: 1 / -1;
  background: #fff8ef;
  box-shadow: inset 4px 0 0 0 #f0a957;
  padding: 6px 16px 14px 16px;
  border-radius: 0 4px 4px 0;
  margin-top: 4px;
}
.stc-section-head--admin {
  border-bottom-color: #e5d4b2;
  color: steelblue;
  padding-top: 8px;
}
.stc-admin-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px 18px;
  align-items: start;
  margin-top: 10px;
}
@media (max-width: 560px) {
  .stc-admin-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 360px) {
  .stc-admin-grid { grid-template-columns: 1fr; }
}

.stc-value { color: #333; word-break: break-word; font-size: 14px; text-align: left; }
.stc-help-note {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
  text-align: left;
}

.stc-footer {
  display: flex;
  gap: 8px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #e0e0e0;
  text-align: left;
}

@media (max-width: 500px) {
  .stc-title { font-size: 18px; }
}

.attachment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.queued-attachment {
  position: relative;
  width: 130px;
  padding: 8px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}

.attachment-thumbnail {
  width: 100%;
  height: 80px;
  object-fit: contain;
  border-radius: 4px;
}

.attachment-file {
  height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

.attachment-filename,
.attachment-name {
  font-size: 0.7rem;
  word-break: break-word;
  text-align: center;
}

.attachment-meta {
  margin-top: 6px;
}

.attachment-name {
  font-size: 0.7rem;
  color: #343a40;
}

.attachment-status {
  font-size: 0.65rem;
  color: #6c757d;
  margin-top: 2px;
}

.attachment-error {
  font-size: 0.65rem;
  color: #c82333;
  margin-top: 2px;
}

.attachment-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  border: none;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 999px;
  width: 20px;
  height: 20px;
  line-height: 1;
  font-size: 0.8rem;
  cursor: pointer;
}

.attachment-state-uploading {
  border-color: #0d6efd;
}

.attachment-state-uploaded {
  border-color: #28a745;
}

.attachment-state-failed {
  border-color: #dc3545;
}
</style>
